import { Module } from 'vuex';

import { statementsService } from '@/services/statements/statements.service';
import { Statement, ItemType, StatementType } from '@/services/statements/statements.types';
import { RootState } from '@/store';
import { Origin } from '@/services/item-statements/item-statements.types';

export interface StatementState {
  statementList: Statement[];
  firstLevel: Statement[];
  childList: Statement[];
  navMenuLoading: boolean;
  selectedStatement: Statement;
  statementLoading: boolean;
}

const state: StatementState = {
  statementList: [],
  firstLevel: [],
  childList: [],
  navMenuLoading: false,
  selectedStatement: null,
  statementLoading: false
};

const options: Module<StatementState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    getAllStatement: ({ commit, dispatch }, model): Promise<void> => {
      commit('navMenuLoading', true);
      return statementsService
        .all(model)
        .then(statementList => statementList && commit('statementList', statementList))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('navMenuLoading', false));
    },
    getFirstLevel: (
      { commit, dispatch },
      options: { statementType: StatementType; itemType: ItemType }
    ): Promise<void> => {
      commit('navMenuLoading', true);
      return statementsService
        .getFirstLevel(options.statementType, options.itemType)
        .then(firstLevel => firstLevel && commit('firstLevel', firstLevel))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('navMenuLoading', false));
    },
    getChilds: ({ commit, dispatch }, options: { statement: Statement; origin: Origin }): Promise<void> => {
      commit('navMenuLoading', true);
      return statementsService
        .getChilds(options.statement.id)
        .then(childs => {
          if (childs.length == 0) dispatch('addItemStatementList', options);
          else childs && commit('childList', childs), dispatch('setSelectedStatementWithId', options.statement.id);
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('navMenuLoading', false));
    },
    addItemStatementList: ({ dispatch }, options: { statement: Statement; origin: Origin }): void => {
      dispatch('setSelectedStatementWithId', options.statement.id).then(() => {
        dispatch(
          'itemStatement/createItemStatementWithStatement',
          { statement: state.selectedStatement, origin: options.origin },
          { root: true }
        ).then(() => dispatch('resetFilter'));
      });
    },
    updateStatement: ({ commit, dispatch }, statement: Statement): Promise<void> => {
      return statementsService
        .update(statement.id, statement)
        .then(statement => statement && commit('selectedStatement', statement))
        .then(() => dispatch('alert/pushSuccess', 'Constat mise à jour !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('navMenuLoading', false));
    },
    deleteStatementList: ({ commit }, index: number): void => {
      commit('deleteStatementList', index);
    },
    setSelectedStatementWithId: ({ commit, dispatch }, statementId: number): Promise<void> => {
      commit('statementLoading', true);
      return statementsService
        .getStatementById(statementId)
        .then(statement => statement && commit('selectedStatement', statement))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('statementLoading', false));
    },
    navBack: ({ dispatch, state }, origin): void => {
      if (state.selectedStatement.parent_id != null) {
        dispatch('setSelectedStatementWithId', state.selectedStatement.parent_id).then(() => {
          dispatch('getChilds', { statement: state.selectedStatement, origin: origin });
        });
      } else {
        dispatch('resetFilter');
      }
    },

    selectStatementItem: ({ commit }, index: number): void => {
      commit('selectStatementItem', index);
    },
    setSelectedStatement: ({ commit }, statement: Statement): void => {
      commit('selectedStatement', statement);
    },

    activeStatement: ({ commit }, statement: Statement): void => {
      commit('activeStatement', statement);
    },

    // CLEAR STATE
    resetFilter: ({ dispatch }): void => {
      dispatch('clearChildList');
      dispatch('clearSelectedStatement');
    },
    clearStatementList: ({ commit }): void => {
      commit('statementList', []);
    },
    clearFirstLevel: ({ commit }): void => {
      commit('firstLevel', []);
    },
    clearChildList: ({ commit }): void => {
      commit('childList', []);
    },
    clearSelectedStatement: ({ commit }): void => {
      commit('selectedStatement', null);
    }
  },
  mutations: {
    statementList: (state, list: Statement[]) => (state.statementList = list),
    firstLevel: (state, list: Statement[]) => (state.firstLevel = list),
    childList: (state, list: Statement[]) => (state.childList = list),
    navMenuLoading: (state, loading: boolean) => (state.navMenuLoading = loading),
    selectedStatement: (state, statement: Statement) => (state.selectedStatement = statement),
    statementLoading: (state, loading: boolean) => (state.statementLoading = loading),
    addStatementPhotos: (state, payload: { item; statement_photos }) =>
      payload.item.statement_photos.push(payload.statement_photos),
    deleteStatementPhotos: (state, payload: { item; statement_photos }) =>
      payload.item.statement_photos.splice(payload.item.statement_photos.indexOf(payload.statement_photos), 1)
  }
};

export default options;
